// The public marketing pages: one entry per indexable URL on photohawk.com, with its title and
// description in each language it's offered in, its link-preview image and its page type. This one
// list drives the server-rendered <head> (RouteMeta), the sitemap and robots.txt, so a new public page
// only needs adding here (plus its route in App.tsx).
//
// Localised pages are offered in English, Spanish and Portuguese at ?lang=es / ?lang=pt (English has
// no parameter and is x-default); the pages made only of site UI copy (/, /pricing, /find-your-photos) also in the
// UI's German, Italian, Japanese, Chinese and Malay. (/find-your-photos' interactive selfie/bib search, opened on
// demand, is still English.) The launch pages (/web-uploader, /preview-first-uploads, /on-demand-ftp) are
// also in German, French, Italian, Dutch, Norwegian, Japanese, Chinese, Malay and Thai (SEO_LANGS). Pages whose
// body is English-only list just "en".
//
// Copy rules (as for the page copy in src/content): never assume the reader's role, spell Photohawk
// with one capital, and make no claim the page itself doesn't make.

import { getWebUploaderCopy, WEB_UPLOADER_PATH } from "@/content/webUploader";
import { getPreviewFirstCopy, PREVIEW_FIRST_PATH } from "@/content/previewFirst";
import { getOnDemandFtpCopy, ON_DEMAND_FTP_PATH } from "@/content/onDemandFtp";
import { getLiveUploadingCopy, LIVE_UPLOADING_PATH } from "@/content/liveUploading";
import { LIVE_UPLOADING_IN_PROD } from "@/lib/liveUploadingFlag";
import { FACE_DATA_CHANGES_PATH, getFaceDataCopy } from "@/content/faceData2026";
import { getPricingCopy } from "@/content/pricing";
import { CHANGES_PUBLIC } from "@/content/pricing/plans";
import { getGdprDoc, getBiometricDoc, BIOMETRIC_POLICY_PATH } from "@/content/legal";
import { FACE_DATA_POLICY_PATH, FAMILY_FACE_DATA_POLICY_PATH, getFaceDataPolicyCopy } from "@/content/faceDataPolicy";
import { readDirectoryParams } from "@/lib/gallery/directory";
import { OG_IMAGES, SEO_LANGS, type OgImage, type SeoLang } from "./site";
import { guidePages } from "./guides";
import { helpPages } from "./help";
import { blogPages } from "./blog";
import { eventPhotographyPages } from "./eventPhotography";
import { APP_PATH, DOWNLOAD_PATH, STUDIO_PATH } from "@/pages/home/paths";
import { getMedia } from "@/content/media";
import { EVENT_TYPE_IMAGES } from "@/content/eventTypes";
import { FOR_ORGANISERS_PATH, ORGANISER_SEO } from "@/content/forOrganisers";
import { STUDIO_APP_SCREENS } from "@/pages/home/studioAppShots";
import uploaderDesktop2x from "@/assets/web-uploader/uploader-desktop-2x.webp";
import ftpPanel2x from "@/assets/on-demand-ftp/ftp-panel-2x.webp";

/** The Photohawk Friends & Family app's pages: path, title, description. The privacy policy and terms keep their
 *  legal documents' own name ("Photohawk app") until legal review. */
const FAMILY_APP_PAGES: [string, string, string][] = [
  [
    "/personal/privacy",
    "Photohawk app: Privacy Policy | Photohawk",
    "How the Photohawk app handles your data: photos are scanned on your phone, shared photos are end-to-end encrypted, optional cloud face matching, print orders, and your rights.",
  ],
  [
    "/personal/terms",
    "Photohawk app: Terms of Use | Photohawk",
    "The terms of use for the Photohawk app: your account, the Personal and Family plans, sharing, face grouping, prints and acceptable use.",
  ],
  [
    "/personal/delete-account",
    "Delete your Photohawk Friends & Family account | Photohawk",
    "How to delete your Photohawk Friends & Family account and its data, in Friends & Family or by email, and what is deleted and kept.",
  ],
  [
    "/personal/support",
    "Photohawk Friends & Family support | Photohawk",
    "Help with Photohawk Friends & Family: contact support@photohawk.com, manage or cancel your subscription, restore purchases, delete your account, and face data.",
  ],
];

export interface PageSeo {
  title: string;
  description: string;
}

/** An image shown on a page (its body, not the link preview): for the image sitemap and the page's ImageObjects. */
export interface PageImage {
  /** Path under the site origin. */
  path: string;
  width: number;
  height: number;
  alt: string;
  caption?: string;
}

export interface PublicPage {
  /** The canonical path. */
  path: string;
  /** Other paths that render the same page; their canonical is `path` and they stay out of the sitemap. */
  aliases?: string[];
  /** Languages the page is offered in ("en" first). */
  langs: readonly SeoLang[];
  /** Title + description in a language from `langs`. */
  seo: (lang: SeoLang) => PageSeo;
  image: OgImage;
  /** The images the page shows (hero first), listed in the image sitemap and as ImageObjects. */
  images?: PageImage[];
  /** Open Graph type. */
  type: "website" | "article";
  /** Whether search engines may index it (and the sitemap lists it). */
  indexable: boolean;
  /**
   * Languages from `langs` that the page renders in but keeps out of search: noindex, no hreflang alternate, not in
   * the sitemap. The help centre's machine translations until a native speaker reviews them.
   */
  noindexLangs?: readonly SeoLang[];
  /** Sitemap hints. */
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  priority: number;
  /**
   * Query parameters that make a different page of this one (a listing's ?country= and ?before=): kept, in this
   * order, in its canonical and hreflang URLs, and allowed in robots.txt. Every other parameter is stripped.
   */
  canonicalParams?: readonly string[];
  /** Last modified (YYYY-MM-DD), for the sitemap's <lastmod>. */
  lastmod?: string;
  /** JSON-LD nodes for this page, replacing the default WebPage / Article node (the Organization is always added). */
  structuredData?: (ctx: StructuredDataContext) => object[];
}

export interface StructuredDataContext {
  origin: string;
  /** This URL (canonical). */
  url: string;
  lang: SeoLang;
  title: string;
  description: string;
  /** Absolute link-preview image URL. */
  image: string;
}

const ALL: readonly SeoLang[] = ["en", "es", "pt"];
/** The site UI's languages (src/i18n): pages built only from UI copy (the home page, /pricing, /find-your-photos) render
 *  fully in each of them, so they're offered in all eight. */
const UI: readonly SeoLang[] = ["en", "es", "pt", "de", "it", "ja", "zh", "ms"];
/** The launch pages: every language in SEO_LANGS (their copy is in src/content/<page>/copy.<lang>.ts). */
const LAUNCH: readonly SeoLang[] = SEO_LANGS;
const EN: readonly SeoLang[] = ["en"];

/** A content image (src/content/media) as a page image, with its own alt text and caption. */
const mediaImage = (name: string): PageImage => {
  const m = getMedia(name);
  if (!m) throw new Error(`no content image ${name}`);
  return { path: m.src, width: m.width, height: m.height, alt: m.alt, caption: m.caption };
};

/** A landing page's 1200x630 link-preview card (scripts/content-images/render.mjs). */
const pageCard = (name: string, alt: string): OgImage => ({ path: `/images/og/pages/${name}.jpg`, width: 1200, height: 630, type: "image/jpeg", alt });

const byLang = (m: { en: PageSeo } & Partial<Record<SeoLang, PageSeo>>) => (lang: SeoLang) => m[lang] ?? m.en;
const enOnly = (s: PageSeo) => () => s;

export const PUBLIC_PAGES: PublicPage[] = [
  {
    path: "/",
    langs: UI,
    seo: byLang({
      en: {
        title: "Photohawk | Share and sell event photos by face or bib",
        description:
          "Get your event photos online fast. Share them for free or sell them, and let everyone find their photos by face or bib number, with branded galleries and secure checkout.",
      },
      es: {
        title: "Photohawk | Comparte y vende fotos de eventos",
        description:
          "Pon tus fotos de eventos en línea en poco tiempo. Compártelas gratis o véndelas, y deja que cada persona encuentre sus fotos por su cara o su número de dorsal, con galerías de tu marca y pago seguro.",
      },
      pt: {
        title: "Photohawk | Compartilhe e venda fotos de eventos",
        description:
          "Coloque suas fotos de eventos online rapidamente. Compartilhe gratuitamente ou venda, e deixe cada pessoa encontrar suas fotos pelo rosto ou pelo número de peito, com galerias da sua marca e pagamento seguro.",
      },
      de: {
        title: "Photohawk | Eventfotos teilen und verkaufen",
        description:
          "Bringen Sie Ihre Eventfotos schnell online. Teilen Sie sie kostenlos oder verkaufen Sie sie, und alle finden ihre Fotos per Gesicht oder Startnummer, mit Galerien in Ihrem Branding und sicherem Bezahlvorgang.",
      },
      it: {
        title: "Photohawk | Condividi e vendi foto di eventi",
        description:
          "Metti online le foto dei tuoi eventi in poco tempo. Condividile gratis o vendile, e ognuno trova le proprie foto con il volto o il numero di pettorale, in gallerie con il tuo marchio e con pagamenti sicuri.",
      },
      ja: {
        title: "Photohawk | イベント写真を共有・販売",
        description:
          "イベント写真をすばやくオンラインに。無料で共有することも販売することもでき、誰もが顔やゼッケン番号で自分の写真を見つけられます。独自ブランドのギャラリーと安全な決済にも対応。",
      },
      zh: {
        title: "Photohawk | 分享和销售活动照片",
        description:
          "快速将活动照片上线。免费分享或出售照片，让每个人都能通过人脸或号码布找到自己的照片，并提供品牌化画廊和安全结账。",
      },
      ms: {
        title: "Photohawk | Kongsi dan jual foto acara",
        description:
          "Letakkan foto acara anda dalam talian dengan pantas. Kongsi secara percuma atau jual, dan biarkan semua orang mencari foto mereka melalui wajah atau nombor bib, dengan galeri berjenama dan pembayaran selamat.",
      },
    }),
    image: OG_IMAGES.default,
    type: "website",
    indexable: true,
    changefreq: "weekly",
    priority: 1.0,
  },
  {
    path: "/pricing",
    // The pricing from 1 November 2026 (src/pages/Pricing2026.tsx): its copy (src/content/pricing) is in all eight
    // site languages.
    langs: UI,
    seo: (lang) => getPricingCopy(lang).pricing.seo,
    image: OG_IMAGES.pricing,
    type: "website",
    indexable: true,
    changefreq: "weekly",
    priority: 0.9,
  },
  {
    path: "/pricing-changes-2026",
    langs: ALL,
    seo: (lang) => getPricingCopy(lang).changes.seo,
    image: OG_IMAGES.pricingChanges,
    type: "article",
    // Public from the 20 October 2026 announcement (CHANGES_PUBLIC).
    indexable: CHANGES_PUBLIC,
    changefreq: "weekly",
    priority: 0.8,
  },
  {
    // Linked from the face-data campaign email (en, es, pt like /pricing-changes-2026).
    path: FACE_DATA_CHANGES_PATH,
    langs: ALL,
    seo: (lang) => getFaceDataCopy(lang).seo,
    image: OG_IMAGES.default,
    type: "article",
    indexable: true,
    changefreq: "monthly",
    priority: 0.7,
    lastmod: "2026-10-19",
  },
  {
    path: WEB_UPLOADER_PATH,
    langs: LAUNCH,
    seo: (lang) => getWebUploaderCopy(lang).seo,
    image: OG_IMAGES.webUploader,
    // The hero screenshot (src/pages/WebUploader.tsx), its 2x file.
    images: [{ path: uploaderDesktop2x, width: 2400, height: 1500, alt: getWebUploaderCopy("en").hero.screenshotAlt }],
    type: "article",
    indexable: true,
    changefreq: "monthly",
    priority: 0.8,
  },
  {
    path: PREVIEW_FIRST_PATH,
    langs: LAUNCH,
    seo: (lang) => getPreviewFirstCopy(lang).seo,
    image: OG_IMAGES.previewFirst,
    type: "article",
    indexable: true,
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: ON_DEMAND_FTP_PATH,
    langs: LAUNCH,
    seo: (lang) => getOnDemandFtpCopy(lang).seo,
    image: OG_IMAGES.onDemandFtp,
    images: [mediaImage("on-demand-ftp-server-panel")],
    type: "article",
    indexable: true,
    changefreq: "monthly",
    priority: 0.6,
  },
  // Upload live from your camera with CloudTether (English and Spanish): listed once it's on in prod
  // (src/lib/liveUploadingFlag.ts: staging only until CloudTether's screenshots are in).
  ...(LIVE_UPLOADING_IN_PROD
    ? [
        {
          path: LIVE_UPLOADING_PATH,
          langs: ["en", "es"] as const,
          seo: (lang: SeoLang) => getLiveUploadingCopy(lang).seo,
          image: {
            path: "/images/og/live-uploading.jpg",
            width: 1200,
            height: 630,
            type: "image/jpeg",
            alt: "CloudTether and Photohawk: every JPEG from the finish line lands in your Photohawk gallery while you keep shooting.",
          },
          type: "article" as const,
          indexable: true,
          changefreq: "monthly" as const,
          priority: 0.6,
        },
      ]
    : []),
  {
    path: "/preview-protection",
    langs: EN,
    seo: enOnly({
      title: "Preview protection: previews that sell | Photohawk",
      description:
        "See how Photohawk's preview protection works: people are softly blurred in thumbnails and more strongly when a photo is opened, faces stay clear, and buyers tap a person to see them clearly — so AI watermark removers have nothing to work with.",
    }),
    image: pageCard("preview-protection", "Preview protection: previews that sell. People are softly blurred in previews, faces stay clear."),
    // The before / now pair the page shows (src/pages/PreviewProtection.tsx).
    images: [
      { path: "/images/demo/preview-protection/preview.jpg", width: 1200, height: 675, alt: "Now: the same preview with people softly blurred and faces clear" },
      { path: "/images/demo/preview-protection/before.jpg", width: 1200, height: 675, alt: "Before: the whole photo, with only a watermark over it" },
    ],
    type: "article",
    indexable: true,
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: "/for-photographers",
    langs: EN,
    seo: enOnly({
      title: "Sell event and race photos online | Photohawk",
      description:
        "Upload, watermark and sell event photos with Photohawk: bib and face search, branded galleries, secure checkout and flexible plans. Start free.",
    }),
    image: pageCard("for-photographers", "Photohawk Studio: share and sell your event photos, found by bib or selfie."),
    images: [mediaImage("web-uploader-live-on-previews")],
    type: "website",
    indexable: true,
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: FOR_ORGANISERS_PATH,
    langs: EN,
    seo: enOnly(ORGANISER_SEO),
    image: pageCard("for-organisers", "Photohawk Studio for organisers: event photos for every participant, found by selfie, number or time."),
    images: [
      mediaImage("bib-number-search-dialog"),
      mediaImage("selfie-search-results"),
      ...EVENT_TYPE_IMAGES.map(mediaImage),
      mediaImage("sponsor-free-download-photos-diagram"),
      mediaImage("face-search-gdpr-checklist-diagram"),
    ],
    type: "website",
    indexable: true,
    changefreq: "monthly",
    priority: 0.7,
    lastmod: "2026-10-27",
  },
  {
    path: "/for-participants",
    langs: EN,
    seo: enOnly({
      title: "Find your race photos | Photohawk",
      description:
        "Find your event and race photos by bib number, selfie or clothing colour, then buy and download them with secure checkout.",
    }),
    image: pageCard("for-participants", "Find your race photos by bib number or a selfie, with Photohawk."),
    images: [mediaImage("selfie-search-results")],
    type: "website",
    indexable: true,
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: "/find-your-photos",
    langs: UI,
    // The directory's listings: a country (?country=gb) and its older pages (?before=…), src/lib/gallery/directory.ts.
    canonicalParams: ["country", "before"],
    seo: byLang({
      en: {
        title: "Find your photos | Photohawk",
        description: "Search event galleries on Photohawk to find your race day photos by face or bib number.",
      },
      es: {
        title: "Encuentra tus fotos | Photohawk",
        description: "Busca en las galerías de eventos de Photohawk para encontrar las fotos de tu carrera por tu cara o tu dorsal.",
      },
      pt: {
        title: "Encontre suas fotos | Photohawk",
        description: "Pesquise as galerias de eventos da Photohawk para encontrar as fotos da sua corrida pelo rosto ou pelo número de peito.",
      },
      de: {
        title: "Fotos finden | Photohawk",
        description: "Durchsuchen Sie die Eventgalerien auf Photohawk und finden Sie Ihre Fotos vom Renntag per Gesicht oder Startnummer.",
      },
      it: {
        title: "Trova le tue foto | Photohawk",
        description: "Cerca tra le gallerie di eventi su Photohawk e trova le foto della tua gara con il volto o il numero di pettorale.",
      },
      ja: {
        title: "写真を探す | Photohawk",
        description: "Photohawkのイベントギャラリーを検索して、顔やゼッケン番号でレース当日の写真を見つけましょう。",
      },
      zh: {
        title: "查找照片 | Photohawk",
        description: "在 Photohawk 上搜索活动画廊，通过人脸或号码布找到您的比赛照片。",
      },
      ms: {
        title: "Cari foto anda | Photohawk",
        description: "Cari galeri acara di Photohawk untuk mencari foto hari perlumbaan anda melalui wajah atau nombor bib.",
      },
    }),
    image: pageCard("find-your-photos", "Search event galleries for your photos by bib number or a selfie. Photohawk."),
    type: "website",
    indexable: true,
    changefreq: "daily",
    priority: 0.8,
  },
  {
    path: "/about",
    langs: EN,
    seo: enOnly({
      title: "About Photohawk | A small UK team with a global vision",
      description:
        "Meet the remote-first, UK-based team behind Photohawk, the platform for sharing and selling sports and event photos with face and bib search.",
    }),
    image: OG_IMAGES.default,
    type: "website",
    indexable: true,
    changefreq: "yearly",
    priority: 0.5,
  },
  {
    path: "/privacy",
    aliases: ["/rgpd", "/gdpr", "/data-protection"],
    langs: ALL,
    seo: (lang) => ({
      // The policy's own heading, except where it's too long for a title (es/pt: "… (RGPD / GDPR)").
      title: { es: "Política de privacidad (RGPD) | Photohawk", pt: "Política de Privacidade (RGPD) | Photohawk" }[lang as "es" | "pt"] ??
        `${getGdprDoc(lang).title} | Photohawk`,
      description: {
        en: "How Photohawk collects, uses and protects personal data, including face-search data, and how to exercise your rights under the GDPR.",
        es: "Cómo Photohawk recoge, utiliza y protege los datos personales, incluidos los datos de búsqueda facial, y cómo ejercer tus derechos según el RGPD.",
        pt: "Como a Photohawk recolhe, utiliza e protege os dados pessoais, incluindo os dados de pesquisa facial, e como exercer os seus direitos ao abrigo do RGPD.",
      }[lang as "en" | "es" | "pt"],
    }),
    image: OG_IMAGES.default,
    type: "article",
    indexable: true,
    changefreq: "yearly",
    priority: 0.3,
  },
  {
    // Photohawk's face data policy (Studio and app tabs), public from 20 October 2026, en/es/pt.
    path: FACE_DATA_POLICY_PATH,
    langs: ALL,
    seo: (lang) => getFaceDataPolicyCopy(lang).seo,
    image: OG_IMAGES.default,
    type: "article",
    indexable: true,
    changefreq: "yearly",
    priority: 0.4,
    lastmod: "2026-10-20",
  },
  {
    // The full Photohawk app face data policy (English only; the app's /personal/face-data-policy is a 301 here).
    path: FAMILY_FACE_DATA_POLICY_PATH,
    langs: EN,
    seo: enOnly({ title: getFaceDataPolicyCopy("en").familyPage.seoTitle, description: getFaceDataPolicyCopy("en").familyPage.description }),
    image: OG_IMAGES.default,
    type: "article",
    indexable: true,
    changefreq: "yearly",
    priority: 0.3,
    lastmod: "2026-10-20",
  },
  // The Photohawk app's own pages, public in production from 20 October 2026 (the App Store listing and the
  // app link them; src/pages/personal). English only.
  ...FAMILY_APP_PAGES.map(
    ([path, title, description]): PublicPage => ({
      path,
      langs: EN,
      seo: enOnly({ title, description }),
      image: OG_IMAGES.default,
      type: "article",
      indexable: true,
      changefreq: "yearly",
      priority: 0.3,
      lastmod: "2026-10-20",
    }),
  ),
  {
    path: BIOMETRIC_POLICY_PATH,
    langs: EN,
    seo: enOnly({
      title: `${getBiometricDoc("en").title} | Photohawk`,
      description:
        "How long Photohawk keeps face-search data and when it is deleted: for the period the gallery's administrator chooses (No expiry by default), when a gallery is deleted, 30 days after face search is switched off, or within 30 days of a deletion request. Selfies are never stored.",
    }),
    image: OG_IMAGES.default,
    type: "article",
    indexable: true,
    changefreq: "yearly",
    priority: 0.3,
  },
];

// ---------- The new site (staging only until launch: src/lib/homeFlag.ts) ----------
// One brand, one story, Studio first (owner, 2 Nov 2026): "/" leads with Photohawk Studio and keeps today's "/" title
// and description (in every language), "/studio" is Photohawk Studio's full page (titled "Photohawk Studio | …"; it
// was /events, now a 301), "/app" is the Photohawk app (its URL was /personal, now a 301) and "/download" the Studio
// apps (English only). Where the new home is off (prod), PUBLIC_PAGES above is the list, unchanged.

/** "/app": the Photohawk app ("Photohawk: Friends & Family" on the App Store). English only until the page's copy is
 *  translated (src/pages/home/pendingTranslations.ts ".app."). No prices; never Studio, events or selling. */
/** /app's own link-preview card (the app page's hero photo; scripts/content-images/render.mjs), never an event photo. */
const APP_PAGE_CARD = pageCard("photohawk-app", "Photohawk app: Your people, in every photo");

export const APP_PAGE_SEO = enOnly({
  title: "Photohawk: Friends & Family | Your people, in every photo",
  description:
    "Photohawk finds the people in your camera roll on your iPhone, so you can find any photo by who is in it and share photos with the people in them. Faces are found on your phone. Free to start, with a 7-day free trial.",
});

/** "/download": the Photohawk Studio app, launching November 2026 (English only for now). Never says it can be
 *  downloaded today: Studio runs in the browser until then. */
export const DOWNLOAD_SEO = enOnly({
  title: "Photohawk Studio app | Coming to Mac and iPhone in November 2026",
  description:
    "The Photohawk Studio app for Mac, iPhone and iPad launches in November 2026, with live camera tethering. Today, Photohawk Studio runs in your web browser, with nothing to install. Windows later.",
});

/** The new site's Studio page (today's "/" description, titled for Studio), the app page and the download page
 *  (src/pages/home/paths.ts). */
export { STUDIO_PATH as STUDIO_PAGE_PATH, APP_PATH as APP_PAGE_PATH, DOWNLOAD_PATH as DOWNLOAD_PAGE_PATH };

const HOME = PUBLIC_PAGES.find((p) => p.path === "/")!;

/** The real product screens /studio shows (src/pages/home/StudioSections.tsx), for the image sitemap and its ImageObjects. */
const STUDIO_IMAGES: PageImage[] = [
  { path: STUDIO_APP_SCREENS.macUpload.src, width: 1080, height: 671, alt: "The Studio app's Upload screen on a Mac: race photos picked from a card, three collections to send them to, and a queue uploading 8 of 9 photos." },
  { path: STUDIO_APP_SCREENS.macGalleries.src, width: 1080, height: 671, alt: "A race gallery in the Studio app on a Mac: 2,214 photos in a grid, with search by bib number or text and the Start line, Mile 3 and Finish collections." },
  { path: STUDIO_APP_SCREENS.macOrders.src, width: 1080, height: 671, alt: "An order in the Studio app on a Mac with the Refund this order dialog open: a partial refund of $12.00 of $36.00." },
  mediaImage("event-gallery-photo-grid"),
  mediaImage("selfie-search-results"),
  // the event types grid
  ...EVENT_TYPE_IMAGES.map(mediaImage),
];

/** Today's "/" title and description, with the title naming Photohawk Studio ("Photohawk Studio | …"). */
const studioSeo = (lang: SeoLang): PageSeo => {
  const s = HOME.seo(lang);
  return { ...s, title: s.title.replace(/^Photohawk \|/, "Photohawk Studio |") };
};

/** The marketing pages for a deployment, without the blog and the guides: today's list, or with the new site's
 *  pages where it's on (src/lib/homeFlag.ts). */
export const sitePages = (newHome: boolean): PublicPage[] =>
  newHome
    ? PUBLIC_PAGES.flatMap((p) =>
        p.path === "/"
          ? [
              HOME,
              { ...HOME, path: STUDIO_PATH, seo: studioSeo, priority: 0.9, images: STUDIO_IMAGES },
              { ...HOME, path: APP_PATH, langs: EN, seo: APP_PAGE_SEO, priority: 0.8, image: APP_PAGE_CARD, images: undefined },
              { ...HOME, path: DOWNLOAD_PATH, langs: EN, seo: DOWNLOAD_SEO, priority: 0.7 },
            ]
          : [p],
      )
    : PUBLIC_PAGES;

export const publicPages = (newHome: boolean, guides = false, help = false): PublicPage[] => [
  ...sitePages(newHome),
  // The restored blog is live everywhere; with the guides on, posts a guide replaced are 301s and drop out.
  ...blogPages(guides),
  // The event photography pages (by sport and for organisers): live everywhere.
  ...eventPhotographyPages(),
  ...(guides ? guidePages() : []),
  // The help centre (/help): staging only until the owner approves the imported articles (src/lib/helpFlag.ts).
  ...(help ? helpPages() : []),
];

/** A listing's own query parameters in a URL, validated and in canonical order ([] for every other page). */
export const canonicalQuery = (page: PublicPage, search: string): [string, string][] => {
  if (!page.canonicalParams?.length) return [];
  const { country, before } = readDirectoryParams(search);
  const values: Record<string, string | null> = { country: country?.toLowerCase() ?? null, before };
  return page.canonicalParams.flatMap((k) => (values[k] ? [[k, values[k]!] as [string, string]] : []));
};

const normalise = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path) || "/";

/** The public page a path renders (its canonical path or an alias), if any. `newHome`: src/lib/homeFlag.ts;
 *  `guides`: src/lib/guidesFlag.ts. */
export const findPublicPage = (path: string, newHome = false, guides = false, help = false): PublicPage | undefined => {
  const p = normalise(path);
  return publicPages(newHome, guides, help).find((page) => page.path === p || page.aliases?.includes(p));
};

/** The languages of `page` that search engines may index (its `langs` without its `noindexLangs`). */
export const indexedLangs = (page: Pick<PublicPage, "langs" | "noindexLangs">): readonly SeoLang[] =>
  page.noindexLangs?.length ? page.langs.filter((l) => !page.noindexLangs!.includes(l)) : page.langs;
